// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from './authService';
import { getGateSession, GateSession } from './gateSession';
import {
  getMasterSections,
  getMasterItems,
  createMasterItem,
  updateMasterItem,
  deleteMasterItem,
  createMasterSection,
  updateMasterSection,
  getYachtItems,
  bulkUpdateYachtItems,
  createYachtItem,
  deleteYachtItem,
  translateItemToAllLanguages,
} from './services/apiService';

// Gate ζει πλέον στο AdminDashboard. Εδώ διαβάζουμε μόνο το session από το
// in-memory store ώστε τα writes να στέλνουν gateSession.code ως X-Employee-Code.
const useGate = (): GateSession => getGateSession() || { code: '', name: '', role: '' };

// ─────────────────────────────────────────────
// Hardcoded Vessels (όπως στο spec)
// ─────────────────────────────────────────────
const VESSELS = [
  { id: 1, name: 'Maria 1',   type: 'Monohull',  length: 42, capacity: 10 },
  { id: 2, name: 'Maria 2',   type: 'Monohull',  length: 42, capacity: 12 },
  { id: 3, name: 'Valesia',   type: 'Monohull',  length: 42, capacity: 8  },
  { id: 4, name: 'Bar Bar',   type: 'Monohull',  length: 46, capacity: 10 },
  { id: 5, name: 'Kalispera', type: 'Monohull',  length: 42, capacity: 8  },
  { id: 6, name: 'Infinity',  type: 'Catamaran', length: 42, capacity: 10 },
  { id: 7, name: 'Perla',     type: 'Catamaran', length: 42, capacity: 12 },
  { id: 8, name: 'Bob',       type: 'Catamaran', length: 46, capacity: 12 },
];

const PAGE_GROUPS = ['page2', 'page3', 'page4'];
const PAGE_LABELS = { page2: 'Page 2', page3: 'Page 3', page4: 'Page 4' };

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function handleApiError(err) {
  if (!err) {
    alert('Άγνωστο σφάλμα.');
    return;
  }
  if (err.status === 401) {
    alert('Παρακαλώ συνδέσου ξανά');
    return;
  }
  if (err.status === 500) {
    alert('Σφάλμα διακομιστή. Δοκίμασε ξανά.');
    return;
  }
  if (err.message && err.message.toLowerCase().includes('failed to fetch')) {
    alert('Πρόβλημα σύνδεσης.');
    return;
  }
  alert('Σφάλμα: ' + (err.message || 'Άγνωστο σφάλμα'));
}

const EMPTY_ITEM = {
  id: null,
  section: '',
  item_key: '',
  label_el: '',
  label_en: '',
  label_it: '',
  label_de: '',
  label_ru: '',
  label_fr: '',
  label_ro: '',
  label_pl: '',
  label_he: '',
  label_es: '',
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
const ItemsManagementPage: React.FC = () => {
  const navigate = useNavigate();

  // Permission check
  useEffect(() => {
    if (!authService.canManageFleet() && !authService.isTechnical()) {
      navigate('/admin');
    }
  }, [navigate]);

  // Gate γίνεται πλέον στο AdminDashboard. Αν κάποιος φτάσει εδώ απευθείας
  // (direct URL / reload) χωρίς session → πίσω στο /admin για re-gate.
  const gate = getGateSession();
  useEffect(() => {
    if (!gate) navigate('/admin');
  }, [gate, navigate]);

  const [activeTab, setActiveTab] = useState<'master' | 'vessel' | 'boxes'>('master');

  if (!gate) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-800">
            🛠️ Διαχείριση Items
          </h1>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1">
              Συνδεδεμένος: {gate.name} ({gate.code}) — {gate.role}
            </span>
            <button
              onClick={() => navigate('/admin')}
              className="px-3 py-2 text-sm sm:text-base bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
            >
              ← Πίσω
            </button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-2 border-t border-gray-100">
          <button
            onClick={() => setActiveTab('master')}
            className={`px-4 py-3 text-sm sm:text-base font-medium border-b-2 transition ${
              activeTab === 'master'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 Master Catalog
          </button>
          <button
            onClick={() => setActiveTab('vessel')}
            className={`px-4 py-3 text-sm sm:text-base font-medium border-b-2 transition ${
              activeTab === 'vessel'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            ⚙️ Per Vessel
          </button>
          <button
            onClick={() => setActiveTab('boxes')}
            className={`px-4 py-3 text-sm sm:text-base font-medium border-b-2 transition ${
              activeTab === 'boxes'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📦 Πλαίσια
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {activeTab === 'master' ? (
          <MasterCatalogTab />
        ) : activeTab === 'boxes' ? (
          <BoxesTab />
        ) : (
          <PerVesselTab />
        )}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════
// TAB 1: MASTER CATALOG
// ═════════════════════════════════════════════
const MasterCatalogTab: React.FC = () => {
  const gate = useGate();
  const [masterItems, setMasterItems] = useState<any[]>([]);
  const [masterSections, setMasterSections] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [movingItem, setMovingItem] = useState<any | null>(null);
  const [creatingItem, setCreatingItem] = useState(false);
  const [editingSection, setEditingSection] = useState<any | null>(null);
  const [creatingSectionFor, setCreatingSectionFor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkMoving, setBulkMoving] = useState(false);

  // ── Local reorder (v6.4xx): το item επιλέγεται (μπλε), τα βελάκια το μετακινούν
  // ΤΟΠΙΚΑ χωρίς fetch/loadAll (καθόλου scroll jump). Save (flushOrder) γίνεται μόνο
  // όταν αλλάξει η επιλογή ή φύγει ο χρήστης από το tab (unmount cleanup).
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [pendingOrderChanged, setPendingOrderChanged] = useState(false);
  const masterItemsRef = useRef<any[]>([]);
  const pendingRef = useRef(false);
  const dirtySectionsRef = useRef<Set<string>>(new Set());
  const gateRef = useRef(gate);
  gateRef.current = gate;
  useEffect(() => {
    masterItemsRef.current = masterItems;
  }, [masterItems]);
  const toggleSelect = (id: number) =>
    setSelectedIds((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const setSectionSelected = (ids: number[], on: boolean) =>
    setSelectedIds((p) => {
      const n = new Set(p);
      ids.forEach((id) => (on ? n.add(id) : n.delete(id)));
      return n;
    });
  const clearSelection = () => setSelectedIds(new Set());

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [secs, items] = await Promise.all([
        getMasterSections(),
        getMasterItems(),
      ]);
      setMasterSections(secs || []);
      setMasterItems(items || []);
    } catch (err) {
      console.error(err);
      setError('Αποτυχία φόρτωσης δεδομένων.');
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  // Reorder master_items — ΤΟΠΙΚΟ swap μέσα στο masterItems array. ΚΑΝΕΝΑ fetch εδώ,
  // κανένα loadAll: η λίστα δεν ξηλώνεται, το scroll μένει στη θέση του.
  // Το POST (?action=reorder, body {items:[{id,sort_order},...]}) φεύγει στο flushOrder().
  const handleReorderItem = (sectionItems: any[], idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= sectionItems.length) return;
    const a = sectionItems[idx], b = sectionItems[j];
    setMasterItems((prev) => {
      const next = [...prev];
      const ia = next.findIndex((x) => x.id === a.id);
      const ib = next.findIndex((x) => x.id === b.id);
      if (ia < 0 || ib < 0) return prev;
      const tmp = next[ia];
      next[ia] = next[ib];
      next[ib] = tmp;
      masterItemsRef.current = next;
      return next;
    });
    dirtySectionsRef.current.add(a.section || '_other');
    dirtySectionsRef.current.add(b.section || '_other');
    pendingRef.current = true;
    setPendingOrderChanged(true);
    setSelectedItemId(a.id); // η επιλογή ακολουθεί το item που μετακινήθηκε
  };

  // Στέλνει ΟΛΟ το/τα section(s) που πειράχτηκαν με συνεχόμενα sort_order (index+1).
  // Καλείται μόνο σε αλλαγή επιλογής / έξοδο. Δεν κάνει loadAll — η τοπική σειρά είναι ήδη σωστή.
  const flushOrder = async () => {
    if (!pendingRef.current) return;
    const dirty = dirtySectionsRef.current;
    const counters: Record<string, number> = {};
    const payload: { id: number; sort_order: number }[] = [];
    masterItemsRef.current.forEach((it: any) => {
      const sec = it.section || '_other';
      counters[sec] = (counters[sec] || 0) + 1;
      if (dirty.has(sec)) payload.push({ id: it.id, sort_order: counters[sec] });
    });
    pendingRef.current = false;
    dirtySectionsRef.current = new Set();
    setPendingOrderChanged(false);
    if (!payload.length) return;
    try {
      const res = await fetch('/api/master-items.php?action=reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Employee-Code': gateRef.current.code },
        body: JSON.stringify({ items: payload }),
      });
      const r = await res.json();
      if (!r.success) alert('Σφάλμα ταξινόμησης: ' + (r.error || ''));
    } catch (e: any) {
      handleApiError(e);
    }
  };

  // Click σε item: flush τυχόν pending σειρά, μετά αλλάζει/καθαρίζει η επιλογή.
  const handleSelectItem = async (id: number) => {
    await flushOrder();
    setSelectedItemId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    loadAll();
    // unmount (tab change / έξοδος) → save ό,τι έμεινε
    return () => {
      void flushOrder();
    };
  }, []);

  // Search filter
  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return masterItems;
    return masterItems.filter((it) => {
      const key = (it.item_key || '').toLowerCase();
      const el = (it.label_el || '').toLowerCase();
      const en = (it.label_en || '').toLowerCase();
      return key.includes(q) || el.includes(q) || en.includes(q);
    });
  }, [masterItems, searchTerm]);

  // Group by section
  const groupedBySection = useMemo(() => {
    const groups: Record<string, any[]> = {};
    filteredItems.forEach((it) => {
      const sec = it.section || '_other';
      if (!groups[sec]) groups[sec] = [];
      groups[sec].push(it);
    });
    return groups;
  }, [filteredItems]);

  const sectionOrder = useMemo(() => {
    const inSecs = masterSections.map((s) => s.section_key || s.key || s);
    const all = Object.keys(groupedBySection);
    const ordered = [...inSecs];
    const extras = all.filter((s) => !inSecs.includes(s));
    return [...ordered, ...extras];
  }, [masterSections, groupedBySection]);

  // 🔥 Group sections by page_group (page2/page3/page4) for rendering
  const pageGroupOrdered = useMemo(() => {
    const PG_LABELS: Record<string, string> = {
      page2: 'Page 2 — Floorplan',
      page3: 'Page 3 — Equipment & Hull',
      page4: 'Page 4 — Safety, Cabin & Optional',
      other: 'Άλλα',
    };
    const buckets: Record<string, string[]> = { page2: [], page3: [], page4: [], other: [] };
    sectionOrder.forEach((sec) => {
      const meta = masterSections.find((s: any) => (s.section_key || s.key || s) === sec);
      const pg = meta?.page_group;
      if (pg && buckets[pg]) buckets[pg].push(sec);
      else buckets.other.push(sec);
    });
    return (['page2', 'page3', 'page4', 'other'] as const)
      .filter((pg) => buckets[pg].length > 0)
      .map((pg) => ({ pg, label: PG_LABELS[pg], sections: buckets[pg] }));
  }, [sectionOrder, masterSections]);

  const toggleSection = (sec: string) => {
    setCollapsedSections((s) => ({ ...s, [sec]: !s[sec] }));
  };

  const handleDelete = async (id: number) => {
    const ok = window.confirm(
      'Σίγουρα; Δεν μπορεί να διαγραφεί αν χρησιμοποιείται από κάποιο σκάφος.'
    );
    if (!ok) return;
    try {
      await deleteMasterItem(id, gate.code);
      await loadAll();
    } catch (err: any) {
      if (err && err.status === 409) {
        alert('Το item χρησιμοποιείται από κάποιο σκάφος. Πρώτα αφαιρέστε από όλα.');
      } else {
        handleApiError(err);
      }
    }
  };

  const selectedItems = useMemo(
    () => masterItems.filter((i: any) => selectedIds.has(i.id)),
    [masterItems, selectedIds]
  );

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    const ok = window.confirm(
      `Διαγραφή ${selectedItems.length} items;\nΌσα χρησιμοποιούνται από σκάφος θα παραλειφθούν.`
    );
    if (!ok) return;
    let success = 0;
    const skipped: string[] = [];
    for (const it of selectedItems) {
      try {
        await deleteMasterItem(it.id, gate.code);
        success++;
      } catch (err: any) {
        if (err && err.status === 409) {
          skipped.push(it.item_key);
        } else {
          handleApiError(err);
          break;
        }
      }
    }
    alert(
      `🗑️ ${success} διαγράφηκαν\n⚠️ ${skipped.length} σε χρήση (παραλείφθηκαν):\n${skipped.join('\n')}`
    );
    clearSelection();
    await loadAll();
  };

  return (
    <div>
      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white rounded-full shadow-lg px-5 py-3 flex items-center gap-3">
          <span className="text-sm font-medium whitespace-nowrap">
            {selectedIds.size} items επιλεγμένα
          </span>
          <button
            onClick={() => setBulkMoving(true)}
            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 rounded-full transition whitespace-nowrap"
          >
            📦 Μετακίνηση
          </button>
          <button
            onClick={handleBulkDelete}
            className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 rounded-full transition whitespace-nowrap"
          >
            🗑️ Διαγραφή
          </button>
          <button
            onClick={clearSelection}
            className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-500 rounded-full transition whitespace-nowrap"
          >
            ❌ Ακύρωση
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="sticky top-[112px] z-20 bg-gray-50 pb-3">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <input
            type="text"
            placeholder="🔍 Αναζήτηση (key, ελληνικά, αγγλικά)…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => setCreatingItem(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition whitespace-nowrap"
          >
            + Νέο Item
          </button>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Φόρτωση…</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-center">
          {error}
        </div>
      ) : sectionOrder.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          Δεν βρέθηκαν items.
        </div>
      ) : (
        <div className="space-y-6">
          {pageGroupOrdered.map((group) => (
            <div key={group.pg}>
              <h2 className="text-base sm:text-lg font-bold text-gray-700 px-1 mb-2">
                📋 {group.label}
              </h2>
              <div className="space-y-3">
                {group.sections.map((sec) => {
                  const items = groupedBySection[sec] || [];
                  const collapsed = collapsedSections[sec];
                  const meta = masterSections.find((s: any) => (s.section_key || s.key || s) === sec);
                  return (
                    <div
                      key={sec}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                    >
                      <div className="w-full px-4 py-3 flex items-center justify-between gap-2 bg-gray-50 hover:bg-gray-100 transition">
                        <input
                          type="checkbox"
                          className="w-4 h-4 shrink-0"
                          title="Επιλογή όλων στο section"
                          checked={items.length > 0 && items.every((x: any) => selectedIds.has(x.id))}
                          ref={(el) => {
                            if (el)
                              el.indeterminate =
                                items.some((x: any) => selectedIds.has(x.id)) &&
                                !items.every((x: any) => selectedIds.has(x.id));
                          }}
                          onChange={(e) =>
                            setSectionSelected(
                              items.map((x: any) => x.id),
                              e.target.checked
                            )
                          }
                        />
                        <button
                          onClick={() => toggleSection(sec)}
                          className="flex-1 flex items-center gap-2 text-left min-w-0"
                        >
                          <span className="text-gray-400">{collapsed ? '▶' : '▼'}</span>
                          <span className="font-semibold text-gray-800 truncate">
                            {sec} <span className="text-gray-500 font-normal">({items.length} items)</span>
                          </span>
                        </button>
                        {meta && meta.id && (
                          <button
                            onClick={() => setEditingSection(meta)}
                            className="px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg transition whitespace-nowrap"
                          >
                            ✏️ Αλλαγή Ονόματος
                          </button>
                        )}
                      </div>
                      {!collapsed && (
                        <div className="divide-y divide-gray-100">
                          {items.map((it, idx) => {
                            const isPicked = selectedItemId === it.id;
                            return (
                            <div
                              key={it.id}
                              onClick={() => { void handleSelectItem(it.id); }}
                              className={
                                'px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 cursor-pointer transition ' +
                                (isPicked
                                  ? 'bg-blue-300 border-l-4 border-blue-600'
                                  : 'border-l-4 border-transparent hover:bg-gray-50')
                              }
                            >
                              <input
                                type="checkbox"
                                className="w-4 h-4 shrink-0 self-start sm:self-center"
                                checked={selectedIds.has(it.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleSelect(it.id)}
                              />
                              <div className="flex-1 min-w-0">
                                <div className={'text-sm font-mono truncate ' + (isPicked ? 'text-gray-700' : 'text-gray-500')}>
                                  {it.item_key}
                                </div>
                                <div className="text-base font-semibold text-gray-900 truncate">
                                  <span className="mr-2">🇬🇷 {it.label_el}</span>
                                  <span className="text-gray-400">|</span>
                                  <span className="ml-2">🇬🇧 {it.label_en}</span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleReorderItem(items, idx, -1); }}
                                  disabled={!isPicked || idx === 0 || searchTerm.trim() !== ''}
                                  title={
                                    searchTerm.trim() !== ''
                                      ? 'Καθάρισε το search για να ταξινομήσεις'
                                      : !isPicked
                                        ? 'Πάτα πρώτα το item για να το επιλέξεις'
                                        : 'Μετακίνηση πάνω'
                                  }
                                  className="px-2 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  ↑
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleReorderItem(items, idx, 1); }}
                                  disabled={!isPicked || idx === items.length - 1 || searchTerm.trim() !== ''}
                                  title={
                                    searchTerm.trim() !== ''
                                      ? 'Καθάρισε το search για να ταξινομήσεις'
                                      : !isPicked
                                        ? 'Πάτα πρώτα το item για να το επιλέξεις'
                                        : 'Μετακίνηση κάτω'
                                  }
                                  className="px-2 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  ↓
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); void flushOrder(); setEditingItem(it); }}
                                  className="px-3 py-1.5 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-lg transition"
                                >
                                  ✏️ Edit
                                </button>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {group.pg !== 'other' && (
                  <button
                    onClick={() => setCreatingSectionFor(group.pg)}
                    className="w-full px-4 py-3 border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 text-gray-600 hover:text-blue-700 rounded-lg text-sm font-medium transition"
                  >
                    + Νέα Επικεφαλίδα στο {group.label}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bulk Move Modal */}
      {bulkMoving && (
        <BulkMoveModal
          items={selectedItems}
          sections={masterSections}
          onClose={() => setBulkMoving(false)}
          onDone={async () => {
            setBulkMoving(false);
            clearSelection();
            await loadAll();
          }}
        />
      )}

      {/* Move Item Modal */}
      {movingItem && (
        <MoveItemModal
          item={movingItem}
          sections={masterSections}
          onClose={() => setMovingItem(null)}
          onMoved={async () => {
            setMovingItem(null);
            await loadAll();
          }}
        />
      )}
      {/* Modal */}
      {(creatingItem || editingItem) && (
        <MasterItemModal
          mode={creatingItem ? 'create' : 'edit'}
          initial={editingItem || EMPTY_ITEM}
          sections={masterSections}
          onClose={() => {
            setCreatingItem(false);
            setEditingItem(null);
          }}
          onSaved={async () => {
            setCreatingItem(false);
            setEditingItem(null);
            await loadAll();
          }}
        />
      )}

      {/* Section Modals */}
      {creatingSectionFor && (
        <SectionCreateModal
          pageGroup={creatingSectionFor}
          existingSectionKeys={masterSections.map((s: any) => (s.section_key || '').toLowerCase())}
          onClose={() => setCreatingSectionFor(null)}
          onSaved={async () => {
            setCreatingSectionFor(null);
            await loadAll();
          }}
        />
      )}
      {editingSection && (
        <SectionRenameModal
          section={editingSection}
          onClose={() => setEditingSection(null)}
          onSaved={async () => {
            setEditingSection(null);
            await loadAll();
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Move Item Modal — move item to another page+section
function MoveItemModal({ item, sections, onClose, onMoved }: any) {
  const gate = useGate();
  const [targetPageGroup, setTargetPageGroup] = useState<string>(item.page_group || 'page2');
  const [targetSection, setTargetSection] = useState<string>(item.section || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  // Filter sections by selected page_group
  const availableSections = (sections || []).filter((s: any) => s.page_group === targetPageGroup);

  // Auto-select first section when page changes
  React.useEffect(() => {
    if (availableSections.length > 0 && !availableSections.find((s: any) => s.section_key === targetSection)) {
      setTargetSection(availableSections[0].section_key);
    }
  }, [targetPageGroup]);

  const handleMove = async () => {
    if (!targetSection) {
      setError('Επιλέξτε section');
      return;
    }
    if (targetPageGroup === item.page_group && targetSection === item.section) {
      setError('Δεν έχει αλλάξει τίποτα');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateMasterItem(item.id, {
        page_group: targetPageGroup,
        section: targetSection,
      }, gate.code);
      onMoved();
    } catch (e: any) {
      setError(e?.message || 'Αποτυχία μετακίνησης');
      setSaving(false);
    }
  };

  const pageLabels: Record<string, string> = {
    page2: 'Page 2 — Floorplan',
    page3: 'Page 3 — Equipment & Hull',
    page4: 'Page 4 — Safety, Cabin & Optional',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">📦 Μετακίνηση Item</h3>
        <p className="text-sm text-gray-600 mb-1">
          <span className="font-mono">{item.item_key}</span>
        </p>
        <p className="text-sm text-gray-500 mb-4">
          🇬🇷 {item.label_el} | 🇬🇧 {item.label_en}
        </p>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Επιλέξτε Σελίδα:</label>
          <div className="flex flex-col gap-2">
            {(['page2', 'page3', 'page4']).map(pg => (
              <button
                key={pg}
                onClick={() => setTargetPageGroup(pg)}
                className={`px-4 py-2 rounded-lg text-left transition ${targetPageGroup === pg ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                {pageLabels[pg]}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Επιλέξτε Section:</label>
          <select
            value={targetSection}
            onChange={(e) => setTargetSection(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {availableSections.length === 0 ? (
              <option value="">— Δεν υπάρχουν sections —</option>
            ) : (
              availableSections.map((s: any) => (
                <option key={s.section_key} value={s.section_key}>
                  {s.section_key} — {s.title_el || s.title_en}
                </option>
              ))
            )}
          </select>
        </div>

        {error && (
          <div className="mb-3 p-2 bg-red-100 text-red-700 text-sm rounded">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
          >
            Ακύρωση
          </button>
          <button
            onClick={handleMove}
            disabled={saving || !targetSection}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
          >
            {saving ? 'Μετακίνηση...' : '✅ Μετακίνηση'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Bulk Move Modal — move many items to another page+section
function BulkMoveModal({ items, sections, onClose, onDone }: any) {
  const gate = useGate();
  const [targetPageGroup, setTargetPageGroup] = useState<string>('page2');
  const [targetSection, setTargetSection] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const availableSections = (sections || []).filter(
    (s: any) => s.page_group === targetPageGroup
  );

  React.useEffect(() => {
    if (
      availableSections.length > 0 &&
      !availableSections.find((s: any) => s.section_key === targetSection)
    ) {
      setTargetSection(availableSections[0].section_key);
    }
  }, [targetPageGroup]);

  const handleBulkMove = async () => {
    if (!targetSection) {
      setError('Επιλέξτε section');
      return;
    }
    setSaving(true);
    setError('');
    let success = 0;
    const skipped: string[] = [];
    for (const it of items) {
      if (it.page_group === targetPageGroup && it.section === targetSection) {
        skipped.push(it.item_key);
        continue;
      }
      try {
        await updateMasterItem(it.id, {
          page_group: targetPageGroup,
          section: targetSection,
        }, gate.code);
        success++;
      } catch (e: any) {
        skipped.push(it.item_key);
      }
    }
    alert(
      `✅ ${success} μετακινήθηκαν\n⚠️ ${skipped.length} παραλείφθηκαν:\n${skipped.join('\n')}`
    );
    onDone();
  };

  const pageLabels: Record<string, string> = {
    page2: 'Page 2 — Floorplan',
    page3: 'Page 3 — Equipment & Hull',
    page4: 'Page 4 — Safety, Cabin & Optional',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">📦 Μαζική Μετακίνηση</h3>
        <p className="text-sm text-gray-600 mb-4">
          {items.length} items επιλεγμένα
        </p>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Επιλέξτε Σελίδα:</label>
          <div className="flex flex-col gap-2">
            {(['page2', 'page3', 'page4']).map((pg) => (
              <button
                key={pg}
                onClick={() => setTargetPageGroup(pg)}
                className={`px-4 py-2 rounded-lg text-left transition ${
                  targetPageGroup === pg
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {pageLabels[pg]}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Επιλέξτε Section:</label>
          <select
            value={targetSection}
            onChange={(e) => setTargetSection(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {availableSections.length === 0 ? (
              <option value="">— Δεν υπάρχουν sections —</option>
            ) : (
              availableSections.map((s: any) => (
                <option key={s.section_key} value={s.section_key}>
                  {s.section_key} — {s.title_el || s.title_en}
                </option>
              ))
            )}
          </select>
        </div>

        {error && (
          <div className="mb-3 p-2 bg-red-100 text-red-700 text-sm rounded">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
          >
            Ακύρωση
          </button>
          <button
            onClick={handleBulkMove}
            disabled={saving || !targetSection}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
          >
            {saving ? 'Μετακίνηση…' : `✅ Μετακίνηση ${items.length} items`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Master Item Modal (Create/Edit)
// ─────────────────────────────────────────────
const MasterItemModal: React.FC<any> = ({ mode, initial, sections, onClose, onSaved }) => {
  const gate = useGate();
  const isEdit = mode === 'edit';

  const [section, setSection] = useState(initial.section || '');
  const [itemKey, setItemKey] = useState(initial.item_key || '');
  const [labelEl, setLabelEl] = useState(initial.label_el || '');
  const [labelEn, setLabelEn] = useState(initial.label_en || '');
  const [labelIt, setLabelIt] = useState(initial.label_it || '');
  const [labelDe, setLabelDe] = useState(initial.label_de || '');
  const [labelRu, setLabelRu] = useState(initial.label_ru || '');
  const [labelFr, setLabelFr] = useState(initial.label_fr || '');
  const [labelRo, setLabelRo] = useState(initial.label_ro || '');
  const [labelPl, setLabelPl] = useState(initial.label_pl || '');
  const [labelHe, setLabelHe] = useState(initial.label_he || '');
  const [labelEs, setLabelEs] = useState(initial.label_es || '');
  const [hideCheckin, setHideCheckin] = useState(initial.hide_checkin === true || initial.hide_checkin === 't' || initial.hide_checkin === 'true');
  const [hideCheckout, setHideCheckout] = useState(initial.hide_checkout === true || initial.hide_checkout === 't' || initial.hide_checkout === 'true');

  const [loadingTranslate, setLoadingTranslate] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAutoTranslate = async () => {
    if (!labelEl || !labelEn) return;
    setLoadingTranslate(true);
    try {
      const t = await translateItemToAllLanguages(labelEl, labelEn);
      setLabelIt(t.it || '');
      setLabelDe(t.de || '');
      setLabelRu(t.ru || '');
      setLabelFr(t.fr || '');
      setLabelRo(t.ro || '');
      setLabelPl(t.pl || '');
      setLabelHe(t.he || '');
      setLabelEs(t.es || '');
    } catch (err: any) {
      if (err && err.message === 'AI_TRANSLATION_NOT_CONFIGURED') {
        alert(
          '🔑 AI Translation δεν έχει ενεργοποιηθεί ακόμη.\n\n' +
            'Παρακαλώ προσθέστε ANTHROPIC_API_KEY στο /api/config.php.\n\n' +
            'Συμπληρώστε χειροκίνητα τις 8 γλώσσες παρακάτω.'
        );
      } else {
        alert('Σφάλμα μετάφρασης: ' + (err?.message || 'άγνωστο'));
      }
    } finally {
      setLoadingTranslate(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!section || !itemKey || !labelEl || !labelEn) {
      alert('Παρακαλώ συμπληρώστε: Section, item_key, 🇬🇷 ελληνικά, 🇬🇧 αγγλικά.');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(itemKey)) {
      alert('Το item_key επιτρέπει μόνο: μικρά γράμματα, αριθμούς, _');
      return;
    }
    if (itemKey.length > 100) {
      alert('Το item_key πρέπει να είναι μέχρι 100 χαρακτήρες.');
      return;
    }
    if (labelEl.length > 200 || labelEn.length > 200) {
      alert('Τα labels πρέπει να είναι μέχρι 200 χαρακτήρες.');
      return;
    }

    const payload = {
      section,
      item_key: itemKey,
      label_el: labelEl,
      label_en: labelEn,
      label_it: labelIt,
      label_de: labelDe,
      label_ru: labelRu,
      label_fr: labelFr,
      label_ro: labelRo,
      label_pl: labelPl,
      label_he: labelHe,
      label_es: labelEs,
      hide_checkin: hideCheckin,
      hide_checkout: hideCheckout,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateMasterItem(initial.id, payload, gate.code);
      } else {
        await createMasterItem(payload, gate.code);
      }
      await onSaved();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-4">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-800">
            {isEdit ? '✏️ Επεξεργασία Item' : '➕ Νέο Item'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Section *</label>
            <select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              disabled={isEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="">— Επιλέξτε section —</option>
              {sections.map((s: any) => {
                const key = s.section_key || s.key || s;
                return (
                  <option key={key} value={key}>
                    {key}
                  </option>
                );
              })}
            </select>
          </div>

          {/* item_key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              item_key * <span className="text-xs text-gray-400">(lowercase, a-z 0-9 _, max 100)</span>
            </label>
            <input
              type="text"
              value={itemKey}
              onChange={(e) => setItemKey(e.target.value.toLowerCase())}
              maxLength={100}
              disabled={isEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>

          {/* Visibility (master — all vessels) */}
          <div className="flex flex-wrap gap-4 mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={hideCheckin} onChange={(e) => setHideCheckin(e.target.checked)} />
              Κρυφό στο check-in
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={hideCheckout} onChange={(e) => setHideCheckout(e.target.checked)} />
              Κρυφό στο check-out
            </label>
          </div>

          {/* Required labels */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇬🇷 Ελληνικά *</label>
              <input
                type="text"
                value={labelEl}
                onChange={(e) => setLabelEl(e.target.value)}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇬🇧 English *</label>
              <input
                type="text"
                value={labelEn}
                onChange={(e) => setLabelEn(e.target.value)}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* AI Translate */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="text-sm text-blue-900">
              🤖 Αυτόματη μετάφραση στις 8 υπόλοιπες γλώσσες
            </div>
            <button
              type="button"
              onClick={handleAutoTranslate}
              disabled={!labelEl || !labelEn || loadingTranslate}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition whitespace-nowrap"
            >
              {loadingTranslate ? 'Μετάφραση…' : '🤖 Μετάφρασε'}
            </button>
          </div>

          {/* 8 manual translation inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇮🇹 Italiano</label>
              <input type="text" value={labelIt} onChange={(e) => setLabelIt(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇩🇪 Deutsch</label>
              <input type="text" value={labelDe} onChange={(e) => setLabelDe(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇷🇺 Русский</label>
              <input type="text" value={labelRu} onChange={(e) => setLabelRu(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇫🇷 Français</label>
              <input type="text" value={labelFr} onChange={(e) => setLabelFr(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇷🇴 Română</label>
              <input type="text" value={labelRo} onChange={(e) => setLabelRo(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇵🇱 Polski</label>
              <input type="text" value={labelPl} onChange={(e) => setLabelPl(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇮🇱 עברית</label>
              <input type="text" value={labelHe} onChange={(e) => setLabelHe(e.target.value)} maxLength={200} dir="rtl"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇪🇸 Español</label>
              <input type="text" value={labelEs} onChange={(e) => setLabelEs(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white rounded-b-xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition disabled:opacity-50"
          >
            ❌ Ακύρωση
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition disabled:bg-gray-300"
          >
            {saving ? 'Αποθήκευση…' : '💾 Αποθήκευση'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Section Create Modal
// ─────────────────────────────────────────────
const SectionCreateModal: React.FC<any> = ({ pageGroup, existingSectionKeys, onClose, onSaved }) => {
  const gate = useGate();
  const [sectionKey, setSectionKey] = useState('');
  const [titleEl, setTitleEl] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [titleIt, setTitleIt] = useState('');
  const [titleDe, setTitleDe] = useState('');
  const [titleRu, setTitleRu] = useState('');
  const [titleFr, setTitleFr] = useState('');
  const [titleRo, setTitleRo] = useState('');
  const [titlePl, setTitlePl] = useState('');
  const [titleHe, setTitleHe] = useState('');
  const [titleEs, setTitleEs] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!sectionKey || !titleEl || !titleEn) {
      alert('Παρακαλώ συμπληρώστε: section_key, 🇬🇷 ελληνικά, 🇬🇧 αγγλικά.');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(sectionKey)) {
      alert('Το section_key επιτρέπει μόνο: μικρά γράμματα, αριθμούς, _');
      return;
    }
    if (sectionKey.length > 50) {
      alert('Το section_key πρέπει να είναι μέχρι 50 χαρακτήρες.');
      return;
    }
    if (existingSectionKeys.includes(sectionKey.toLowerCase())) {
      alert('Υπάρχει ήδη section με αυτό το key. Επιλέξτε άλλο.');
      return;
    }

    const payload = {
      section_key: sectionKey,
      page_group: pageGroup,
      title_el: titleEl,
      title_en: titleEn,
      title_it: titleIt,
      title_de: titleDe,
      title_ru: titleRu,
      title_fr: titleFr,
      title_ro: titleRo,
      title_pl: titlePl,
      title_he: titleHe,
      title_es: titleEs,
    };

    setSaving(true);
    try {
      await createMasterSection(payload, gate.code);
      await onSaved();
    } catch (err: any) {
      if (err && err.status === 409) {
        alert('Υπάρχει ήδη section με αυτό το key.');
      } else {
        handleApiError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-4">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-800">
            ➕ Νέα Επικεφαλίδα στο {pageGroup}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              section_key * <span className="text-xs text-gray-400">(lowercase, a-z 0-9 _, max 50)</span>
            </label>
            <input
              type="text"
              value={sectionKey}
              onChange={(e) => setSectionKey(e.target.value.toLowerCase())}
              maxLength={50}
              placeholder="π.χ. bedding"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇬🇷 Ελληνικά *</label>
              <input type="text" value={titleEl} onChange={(e) => setTitleEl(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇬🇧 English *</label>
              <input type="text" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇮🇹 Italiano</label>
              <input type="text" value={titleIt} onChange={(e) => setTitleIt(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇩🇪 Deutsch</label>
              <input type="text" value={titleDe} onChange={(e) => setTitleDe(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇷🇺 Русский</label>
              <input type="text" value={titleRu} onChange={(e) => setTitleRu(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇫🇷 Français</label>
              <input type="text" value={titleFr} onChange={(e) => setTitleFr(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇷🇴 Română</label>
              <input type="text" value={titleRo} onChange={(e) => setTitleRo(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇵🇱 Polski</label>
              <input type="text" value={titlePl} onChange={(e) => setTitlePl(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇮🇱 עברית</label>
              <input type="text" value={titleHe} onChange={(e) => setTitleHe(e.target.value)} maxLength={200} dir="rtl"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇪🇸 Español</label>
              <input type="text" value={titleEs} onChange={(e) => setTitleEs(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white rounded-b-xl">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition disabled:opacity-50">
            ❌ Ακύρωση
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition disabled:bg-gray-300">
            {saving ? 'Αποθήκευση…' : '💾 Αποθήκευση'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Section Rename Modal
// ─────────────────────────────────────────────
const SectionRenameModal: React.FC<any> = ({ section, onClose, onSaved }) => {
  const gate = useGate();
  const [titleEl, setTitleEl] = useState(section.title_el || '');
  const [titleEn, setTitleEn] = useState(section.title_en || '');
  const [titleIt, setTitleIt] = useState(section.title_it || '');
  const [titleDe, setTitleDe] = useState(section.title_de || '');
  const [titleRu, setTitleRu] = useState(section.title_ru || '');
  const [titleFr, setTitleFr] = useState(section.title_fr || '');
  const [titleRo, setTitleRo] = useState(section.title_ro || '');
  const [titlePl, setTitlePl] = useState(section.title_pl || '');
  const [titleHe, setTitleHe] = useState(section.title_he || '');
  const [titleEs, setTitleEs] = useState(section.title_es || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!titleEl || !titleEn) {
      alert('Παρακαλώ συμπληρώστε: 🇬🇷 ελληνικά, 🇬🇧 αγγλικά.');
      return;
    }

    const payload = {
      title_el: titleEl,
      title_en: titleEn,
      title_it: titleIt,
      title_de: titleDe,
      title_ru: titleRu,
      title_fr: titleFr,
      title_ro: titleRo,
      title_pl: titlePl,
      title_he: titleHe,
      title_es: titleEs,
    };

    setSaving(true);
    try {
      await updateMasterSection(section.id, payload, gate.code);
      await onSaved();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-4">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-800">
            ✏️ Αλλαγή Ονόματος — <span className="font-mono text-base text-gray-500">{section.section_key}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
            ℹ️ Το <span className="font-mono">section_key</span> δεν αλλάζει (παραμένει <span className="font-mono">{section.section_key}</span>). Αλλάζετε μόνο το εμφανιζόμενο όνομα στις 10 γλώσσες.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇬🇷 Ελληνικά *</label>
              <input type="text" value={titleEl} onChange={(e) => setTitleEl(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇬🇧 English *</label>
              <input type="text" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇮🇹 Italiano</label>
              <input type="text" value={titleIt} onChange={(e) => setTitleIt(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇩🇪 Deutsch</label>
              <input type="text" value={titleDe} onChange={(e) => setTitleDe(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇷🇺 Русский</label>
              <input type="text" value={titleRu} onChange={(e) => setTitleRu(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇫🇷 Français</label>
              <input type="text" value={titleFr} onChange={(e) => setTitleFr(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇷🇴 Română</label>
              <input type="text" value={titleRo} onChange={(e) => setTitleRo(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇵🇱 Polski</label>
              <input type="text" value={titlePl} onChange={(e) => setTitlePl(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇮🇱 עברית</label>
              <input type="text" value={titleHe} onChange={(e) => setTitleHe(e.target.value)} maxLength={200} dir="rtl"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🇪🇸 Español</label>
              <input type="text" value={titleEs} onChange={(e) => setTitleEs(e.target.value)} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white rounded-b-xl">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition disabled:opacity-50">
            ❌ Ακύρωση
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition disabled:bg-gray-300">
            {saving ? 'Αποθήκευση…' : '💾 Αποθήκευση'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════
// TAB 2: PER VESSEL
// ═════════════════════════════════════════════
const PerVesselTab: React.FC = () => {
  const gate = useGate();
  const [selectedVesselId, setSelectedVesselId] = useState<number | null>(null);
  const [vesselSearchTerm, setVesselSearchTerm] = useState('');
  const [innerTab, setInnerTab] = useState<'page2' | 'page3' | 'page4'>('page2');

  const [yachtItemsOriginal, setYachtItemsOriginal] = useState<any[]>([]);
  const [yachtItemsModified, setYachtItemsModified] = useState<Map<number, any>>(new Map());
  const [savingChanges, setSavingChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedVessel = useMemo(
    () => VESSELS.find((v) => v.id === selectedVesselId) || null,
    [selectedVesselId]
  );

  const filteredVessels = useMemo(() => {
    const q = vesselSearchTerm.trim().toLowerCase();
    if (!q) return VESSELS;
    return VESSELS.filter((v) => v.name.toLowerCase().includes(q));
  }, [vesselSearchTerm]);

  const unsavedChanges = yachtItemsModified.size;

  // Load yacht items when vessel changes
  useEffect(() => {
    if (!selectedVesselId) {
      setYachtItemsOriginal([]);
      setYachtItemsModified(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const items = await getYachtItems(selectedVesselId, undefined, true);
        if (!cancelled) {
          setYachtItemsOriginal(items || []);
          setYachtItemsModified(new Map());
        }
      } catch (err) {
        if (!cancelled) {
          setError('Αποτυχία φόρτωσης items.');
          handleApiError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedVesselId]);

  const confirmIfUnsaved = (): boolean => {
    if (unsavedChanges === 0) return true;
    return window.confirm(
      `Έχεις ${unsavedChanges} μη-αποθηκευμένες αλλαγές. Συνέχεια;`
    );
  };

  const handleSelectVessel = (vid: number) => {
    if (!confirmIfUnsaved()) return;
    setSelectedVesselId(vid);
    setVesselSearchTerm('');
  };

  const handleInnerTabChange = (t: 'page2' | 'page3' | 'page4') => {
    if (!confirmIfUnsaved()) return;
    setInnerTab(t);
  };

  // Filter items by current inner tab page_group
  const itemsForCurrentPage = useMemo(() => {
    return yachtItemsOriginal.filter((it) => (it.page_group || '') === innerTab);
  }, [yachtItemsOriginal, innerTab]);

  // Group items by section
  const groupedBySection = useMemo(() => {
    const groups: Record<string, any[]> = {};
    itemsForCurrentPage.forEach((it) => {
      const sec = it.section || '_other';
      if (!groups[sec]) groups[sec] = [];
      groups[sec].push(it);
    });
    return groups;
  }, [itemsForCurrentPage]);

  const sectionOrder = useMemo(() => Object.keys(groupedBySection), [groupedBySection]);

  // Track field change
  const updateField = (id: number, field: 'qty' | 'price' | 'active', value: any) => {
    setYachtItemsModified((prev) => {
      const next = new Map(prev);
      const existing = next.get(id) || {};
      const original = yachtItemsOriginal.find((x) => x.id === id);
      const merged = { ...existing, [field]: value };

      // Check if merged equals original — if so remove from map
      const matchesOriginal =
        (merged.qty === undefined || Number(merged.qty) === Number(original?.qty ?? 0)) &&
        (merged.price === undefined ||
          String(merged.price) === String(original?.price ?? '0')) &&
        (merged.active === undefined || !!merged.active === !!original?.active);

      if (matchesOriginal && Object.keys(merged).length > 0) {
        // Still differs from original on at least one field — check more carefully
        let stillDifferent = false;
        if (merged.qty !== undefined && Number(merged.qty) !== Number(original?.qty ?? 0)) stillDifferent = true;
        if (merged.price !== undefined && String(merged.price) !== String(original?.price ?? '0')) stillDifferent = true;
        if (merged.active !== undefined && !!merged.active !== !!original?.active) stillDifferent = true;
        if (!stillDifferent) {
          next.delete(id);
          return next;
        }
      }
      next.set(id, merged);
      return next;
    });
  };

  const getDisplayValue = (item: any, field: 'qty' | 'price' | 'active') => {
    const mod = yachtItemsModified.get(item.id);
    if (mod && mod[field] !== undefined) return mod[field];
    return item[field];
  };

  const isModified = (id: number) => yachtItemsModified.has(id);

  const handleSaveAll = async () => {
    if (unsavedChanges === 0) return;
    setSavingChanges(true);
    try {
      const updates: any[] = [];
      yachtItemsModified.forEach((changes, id) => {
        updates.push({ id, ...changes });
      });
      await bulkUpdateYachtItems(updates, gate.code);
      // refetch (include_inactive=true so admin can see + re-enable unchecked items)
      const items = await getYachtItems(selectedVesselId!, undefined, true);
      setYachtItemsOriginal(items || []);
      setYachtItemsModified(new Map());
    } catch (err) {
      handleApiError(err);
    } finally {
      setSavingChanges(false);
    }
  };

  const handleCancelAll = () => {
    if (unsavedChanges === 0) return;
    if (!window.confirm(`Ακύρωση ${unsavedChanges} αλλαγών;`)) return;
    setYachtItemsModified(new Map());
  };

  // ─── Render ───
  return (
    <div>
      {/* Vessel Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Επιλογή σκάφους
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder="🔍 Αναζήτηση σκάφους…"
            value={vesselSearchTerm}
            onChange={(e) => setVesselSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {vesselSearchTerm && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredVessels.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">Κανένα σκάφος.</div>
              ) : (
                filteredVessels.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => handleSelectVessel(v.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition"
                  >
                    🚢 {v.name} <span className="text-gray-500">({v.type}, {v.length}ft)</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Quick chip buttons */}
        <div className="mt-3 flex flex-wrap gap-2">
          {VESSELS.map((v) => (
            <button
              key={v.id}
              onClick={() => handleSelectVessel(v.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                selectedVesselId === v.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {v.name}
            </button>
          ))}
        </div>

        {selectedVessel && (
          <div className="mt-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
            🚢 <strong>{selectedVessel.name}</strong> ({selectedVessel.type}, {selectedVessel.length}ft, χωρητικότητα {selectedVessel.capacity})
          </div>
        )}
      </div>

      {/* Inner tabs + body */}
      {!selectedVessel ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          Επίλεξε σκάφος για να δεις τα items του.
        </div>
      ) : (
        <>
          {/* Inner Tabs */}
          <div className="flex gap-2 border-b border-gray-200 mb-4 overflow-x-auto">
            {PAGE_GROUPS.map((p) => (
              <button
                key={p}
                onClick={() => handleInnerTabChange(p as any)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  innerTab === p
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {PAGE_LABELS[p]}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Φόρτωση…</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-center">
              {error}
            </div>
          ) : sectionOrder.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
              Δεν υπάρχουν items για αυτή τη σελίδα.
            </div>
          ) : (
            <div className="space-y-3 pb-24">
              {sectionOrder.map((sec) => {
                const items = groupedBySection[sec] || [];
                return (
                  <div
                    key={sec}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                  >
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 font-semibold text-gray-800">
                      {sec} <span className="text-gray-500 font-normal">({items.length})</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {items.map((it) => {
                        const modified = isModified(it.id);
                        const qty = getDisplayValue(it, 'qty');
                        const price = getDisplayValue(it, 'price');
                        const active = getDisplayValue(it, 'active');
                        return (
                          <div
                            key={it.id}
                            className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 ${
                              modified ? 'bg-yellow-50' : !active ? 'bg-gray-50 opacity-60' : ''
                            }`}
                          >
                            <label className="flex items-center gap-2 sm:w-auto">
                              <input
                                type="checkbox"
                                checked={!!active}
                                onChange={(e) => updateField(it.id, 'active', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm text-gray-700 sm:hidden">Ενεργό</span>
                            </label>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-800 truncate">
                                {it.label_el || it.item_key}
                              </div>
                              <div className="text-xs text-gray-500 font-mono truncate">
                                {it.item_key}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <div className="flex flex-col">
                                <label className="text-xs text-gray-500">Ποσότητα</label>
                                <input
                                  type="number"
                                  min={0}
                                  value={qty ?? 0}
                                  onChange={(e) => updateField(it.id, 'qty', Number(e.target.value))}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div className="flex flex-col">
                                <label className="text-xs text-gray-500">Τιμή</label>
                                <div className="relative">
                                  <input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    value={price ?? 0}
                                    onChange={(e) => updateField(it.id, 'price', e.target.value)}
                                    className="w-24 pl-2 pr-6 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sticky Bottom Bar */}
          {unsavedChanges > 0 && (
            <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-2xl">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="text-sm text-gray-700">
                  ⚠️ <strong>{unsavedChanges}</strong> μη-αποθηκευμένες αλλαγές
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCancelAll}
                    disabled={savingChanges}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition disabled:opacity-50"
                  >
                    ↩️ Ακύρωση
                  </button>
                  <button
                    onClick={handleSaveAll}
                    disabled={savingChanges}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition disabled:bg-gray-300"
                  >
                    {savingChanges ? 'Αποθήκευση…' : '💾 Αποθήκευση όλων'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════
// TAB 3: ΠΛΑΙΣΙΑ (master_boxes admin) — Phase 6a MVP
// ═════════════════════════════════════════════
const BOX_API = '/api/master-boxes.php';
const BOX_LANGS: [string, string][] = [
  ['el', '🇬🇷 EL'], ['en', '🇬🇧 EN'], ['it', '🇮🇹 IT'], ['de', '🇩🇪 DE'],
  ['ru', '🇷🇺 RU'], ['fr', '🇫🇷 FR'], ['ro', '🇷🇴 RO'], ['pl', '🇵🇱 PL'],
  ['he', '🇮🇱 HE'], ['es', '🇪🇸 ES'],
];
const BOX_FIELDS: [string, string][] = [
  ['title', 'Τίτλος'],
  ['description', 'Περιγραφή'],
  ['checkbox_label', 'Ετικέτα Checkbox'],
  ['button_label', 'Κείμενο Κουμπιού'],
];
const BOX_PAGE_GROUPS = ['page2', 'page3', 'page4', 'page5'];
const BOX_PAGE_LABELS: Record<string, string> = {
  page2: 'Page 2', page3: 'Page 3', page4: 'Page 4', page5: 'Page 5',
};
const BOX_COLORS = ['blue', 'orange', 'red', 'green'];

const BoxesTab: React.FC = () => {
  const gate = useGate();
  const [boxes, setBoxes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editBox, setEditBox] = useState<any | null>(null);
  const [moveBox, setMoveBox] = useState<any | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BOX_API}?include_inactive=${showDeleted ? 'true' : 'false'}`, { cache: 'no-store' });
      const j = await res.json();
      const list = j.boxes || (Array.isArray(j) ? j : []);
      setBoxes(list);
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showDeleted]);

  const handleDelete = async (box: any) => {
    if (!window.confirm(`Διαγραφή πλαισίου "${box.box_key}";\n(soft delete — μπορεί να επαναφερθεί)`)) return;
    try {
      const res = await fetch(`${BOX_API}?id=${box.id}`, { method: 'DELETE', headers: { 'X-Employee-Code': gate.code } });
      const j = await res.json();
      if (!j.success) { alert('Σφάλμα διαγραφής: ' + (j.error || '')); return; }
      await load();
    } catch (e: any) { handleApiError(e); }
  };

  const handleRestore = async (box: any) => {
    try {
      const res = await fetch(`${BOX_API}?id=${box.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Employee-Code': gate.code },
        body: JSON.stringify({ active: true }),
      });
      const j = await res.json();
      if (!j.success) { alert('Σφάλμα επαναφοράς: ' + (j.error || '')); return; }
      await load();
    } catch (e: any) { handleApiError(e); }
  };

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const b of boxes) {
      (g[b.page_group] = g[b.page_group] || []).push(b);
    }
    return g;
  }, [boxes]);

  const isDeleted = (x: any) => x.active === false || x.active === 'f' || x.active === 0;

  // Reorder operates ONLY over active boxes (deleted ones are skipped)
  const activeByGroup = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const pg of Object.keys(grouped)) {
      m[pg] = grouped[pg].filter((x: any) => !isDeleted(x));
    }
    return m;
  }, [grouped]);

  const handleReorder = async (activeArr: any[], idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= activeArr.length) return;
    const a = activeArr[idx], b = activeArr[j];
    const aSO = a.sort_order ?? idx, bSO = b.sort_order ?? j;
    const items = aSO === bSO
      ? [{ id: a.id, sort_order: j }, { id: b.id, sort_order: idx }]
      : [{ id: a.id, sort_order: bSO }, { id: b.id, sort_order: aSO }];
    try {
      const res = await fetch(`${BOX_API}?action=reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Employee-Code': gate.code },
        body: JSON.stringify({ items }),
      });
      const r = await res.json();
      if (!r.success) { alert('Σφάλμα ταξινόμησης: ' + (r.error || '')); return; }
      await load();
    } catch (e: any) { handleApiError(e); }
  };

  const orderedGroups = ['page3', 'page4', 'page5', 'page2'].filter((pg) => grouped[pg]?.length);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-gray-800">📦 Διαχείριση Πλαισίων (master_boxes)</h2>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          Εμφάνιση διαγραμμένων
        </label>
      </div>

      {loading && <div className="text-gray-500 py-8 text-center">Φόρτωση…</div>}
      {error && <div className="text-red-600 py-4">Σφάλμα: {error}</div>}

      {!loading && !error && orderedGroups.length === 0 && (
        <div className="text-gray-500 py-8 text-center">Κανένα πλαίσιο.</div>
      )}

      {!loading && !error && orderedGroups.map((pg) => (
        <div key={pg} className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 font-semibold text-gray-800">
            {BOX_PAGE_LABELS[pg] || pg}{' '}
            <span className="text-gray-500 font-normal">({grouped[pg].length})</span>
          </div>
          <div className="divide-y divide-gray-100">
            {grouped[pg].map((b) => {
              const deleted = isDeleted(b);
              const aArr = activeByGroup[pg] || [];
              const aIdx = deleted ? -1 : aArr.findIndex((x: any) => x.id === b.id);
              return (
                <div
                  key={b.id}
                  className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 ${deleted ? 'bg-red-50' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 font-mono truncate">
                      {b.box_key}
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">{b.box_type}</span>
                      <span className="ml-2 px-1.5 py-0.5 rounded text-white" style={{ background: b.style_color || '#888' }}>
                        {b.style_color}
                      </span>
                      {deleted && <span className="ml-2 px-1.5 py-0.5 rounded bg-red-200 text-red-800">διαγραμμένο</span>}
                    </div>
                    <div className="text-sm text-gray-800 truncate">
                      <span className="mr-2">🇬🇷 {b.title_el || '—'}</span>
                      <span className="text-gray-400">|</span>
                      <span className="ml-2">🇬🇧 {b.title_en || '—'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {deleted ? (
                      <button
                        onClick={() => handleRestore(b)}
                        className="px-3 py-1.5 text-sm bg-green-100 hover:bg-green-200 text-green-800 rounded-lg transition"
                      >
                        ♻️ Επαναφορά
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleReorder(aArr, aIdx, -1)}
                          disabled={aIdx <= 0}
                          className="px-2 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Μετακίνηση πάνω"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => handleReorder(aArr, aIdx, 1)}
                          disabled={aIdx < 0 || aIdx >= aArr.length - 1}
                          className="px-2 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Μετακίνηση κάτω"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => setMoveBox(b)}
                          className="px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg transition"
                          title="Μετακίνηση σε άλλη σελίδα"
                        >
                          📦 Μετακίνηση
                        </button>
                        <button
                          onClick={() => setEditBox(b)}
                          className="px-3 py-1.5 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-lg transition"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(b)}
                          className="px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition"
                        >
                          🗑️ Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {editBox && (
        <BoxEditModal
          box={editBox}
          onClose={() => setEditBox(null)}
          onSaved={async () => { setEditBox(null); await load(); }}
        />
      )}
      {moveBox && (
        <BoxMoveModal
          box={moveBox}
          onClose={() => setMoveBox(null)}
          onSaved={async () => { setMoveBox(null); await load(); }}
        />
      )}
    </div>
  );
};

// ── Edit Modal: 4 fields × 10 langs ──────────────
const BoxEditModal: React.FC<any> = ({ box, onClose, onSaved }) => {
  const gate = useGate();
  const [form, setForm] = useState<any>(() => {
    const f: any = { style_color: box.style_color || 'blue', required: box.required !== false && box.required !== 'f', image_url: box.image_url || null, hide_checkin: box.hide_checkin === true || box.hide_checkin === 't', hide_checkout: box.hide_checkout === true || box.hide_checkout === 't' };
    for (const [field] of BOX_FIELDS) {
      for (const [lang] of BOX_LANGS) {
        f[`${field}_${lang}`] = box[`${field}_${lang}`] ?? '';
      }
    }
    return f;
  });
  const [activeLang, setActiveLang] = useState('el');
  const [saving, setSaving] = useState(false);

  const setVal = (k: string, v: any) => setForm((p: any) => ({ ...p, [k]: v }));

  const imgKB = (du: string) => {
    const b = (du.split(',')[1] || du);
    return Math.round((b.length * 3 / 4) / 1024);
  };

  const onPickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert(`Η εικόνα ξεπερνά τα 500KB (${Math.round(file.size / 1024)}KB). Επιλέξτε μικρότερη.`);
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setVal('image_url', reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: any = { style_color: form.style_color, required: !!form.required, hide_checkin: !!form.hide_checkin, hide_checkout: !!form.hide_checkout };
      for (const [field] of BOX_FIELDS) {
        for (const [lang] of BOX_LANGS) {
          const k = `${field}_${lang}`;
          payload[k] = form[k];
        }
      }
      if (box.box_type === 'file_upload') payload.image_url = form.image_url;
      const res = await fetch(`${BOX_API}?id=${box.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Employee-Code': gate.code },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (!j.success) { alert('Σφάλμα αποθήκευσης: ' + (j.error || '')); setSaving(false); return; }
      await onSaved();
    } catch (e: any) {
      handleApiError(e);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold">✏️ Επεξεργασία Πλαισίου</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="text-xs text-gray-500 font-mono mb-4">
          {box.box_key} · {box.box_type} · {BOX_PAGE_LABELS[box.page_group] || box.page_group}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Χρώμα</label>
            <select
              value={form.style_color}
              onChange={(e) => setVal('style_color', e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              {BOX_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={!!form.required}
                onChange={(e) => setVal('required', e.target.checked)}
              />
              Υποχρεωτικό
            </label>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={!!form.hide_checkin}
                onChange={(e) => setVal('hide_checkin', e.target.checked)}
              />
              Κρυφό στο check-in
            </label>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={!!form.hide_checkout}
                onChange={(e) => setVal('hide_checkout', e.target.checked)}
              />
              Κρυφό στο check-out
            </label>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-200">
          {BOX_LANGS.map(([lang, label]) => (
            <button
              key={lang}
              onClick={() => setActiveLang(lang)}
              className={`px-3 py-2 text-sm border-b-2 transition ${
                activeLang === lang
                  ? 'border-blue-600 text-blue-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {BOX_FIELDS.map(([field, fLabel]) => {
            const k = `${field}_${activeLang}`;
            return (
              <div key={k}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{fLabel}</label>
                <textarea
                  value={form[k] ?? ''}
                  onChange={(e) => setVal(k, e.target.value)}
                  rows={field === 'description' ? 4 : 2}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  dir={activeLang === 'he' ? 'rtl' : 'ltr'}
                />
              </div>
            );
          })}
        </div>

        {box.box_type === 'file_upload' && (
          <div className="mt-5 border-t border-gray-200 pt-4">
            <label className="block text-xs font-medium text-gray-600 mb-2">Εικόνα πλαισίου</label>
            {form.image_url ? (
              <div className="mb-2">
                <img
                  src={form.image_url}
                  alt="preview"
                  className="max-h-40 rounded-lg border border-gray-200"
                />
                <div className="text-xs text-gray-500 mt-1">~{imgKB(form.image_url)} KB</div>
                <button
                  type="button"
                  onClick={() => setVal('image_url', null)}
                  className="mt-2 px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg"
                >
                  🗑️ Καθαρισμός εικόνας
                </button>
              </div>
            ) : (
              <div className="text-xs text-gray-400 mb-2">Καμία εικόνα</div>
            )}
            <input type="file" accept="image/*" onChange={onPickImage} className="text-sm" />
            <div className="text-xs text-gray-400 mt-1">Μέγιστο 500KB</div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
          >
            Άκυρο
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:bg-gray-300"
          >
            {saving ? 'Αποθήκευση…' : '💾 Αποθήκευση'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Move Modal: page_group dropdown ──────────────
const BoxMoveModal: React.FC<any> = ({ box, onClose, onSaved }) => {
  const gate = useGate();
  const [target, setTarget] = useState(box.page_group);
  const [saving, setSaving] = useState(false);

  const handleMove = async () => {
    if (target === box.page_group) { onClose(); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BOX_API}?id=${box.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Employee-Code': gate.code },
        body: JSON.stringify({ page_group: target }),
      });
      const j = await res.json();
      if (!j.success) { alert('Σφάλμα μετακίνησης: ' + (j.error || '')); setSaving(false); return; }
      await onSaved();
    } catch (e: any) {
      handleApiError(e);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold mb-1">📦 Μετακίνηση Πλαισίου</h3>
        <div className="text-xs text-gray-500 font-mono mb-4">{box.box_key}</div>
        <label className="block text-sm text-gray-600 mb-1">Σελίδα προορισμού</label>
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm mb-6"
        >
          {BOX_PAGE_GROUPS.map((pg) => (
            <option key={pg} value={pg}>{BOX_PAGE_LABELS[pg]}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
          >
            Άκυρο
          </button>
          <button
            onClick={handleMove}
            disabled={saving}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:bg-gray-300"
          >
            {saving ? 'Μετακίνηση…' : '✅ Μετακίνηση'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemsManagementPage;
